import React, { Suspense, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Modal } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { ErrorBoundary } from '@acast-tech/product-suite-app-shell';
import { BlockOutlined, LoadingOutlined } from '@ant-design/icons';
import { lazyAdapter } from '../../../utils/moduleFederation';
import { trackEvent } from '../../../controllers/TrackingController';
import Loader from '../Loader';
import { useGetMe } from '../../hooks/query/user';
import { useGetShow } from '../../hooks/query/show';
import { useGetEpisode } from '../../hooks/query/episode';
import { useGetNetwork } from '../../hooks/query/network';
import config from '../../../config';
import { useGetFeatures } from '../../hooks/query/features';
import useMessage from '../../hooks/useMessage';

const EmbedCustomizer = lazyAdapter(() => import('EmbedCustomizer/entry'));

const PlayerButton = () => {
  const intl = useIntl();
  const { message } = useMessage();
  const { episodeId } = useParams();
  const { network, isNetworkLoading } = useGetNetwork();
  const { show, isShowLoading } = useGetShow();
  const { episode, isEpisodeLoading } = useGetEpisode();
  const { user, isUserLoading } = useGetMe();
  const { featuresPermissions, isFeaturesPermissionsLoading } = useGetFeatures();

  const [modalVisible, setModalVisible] = useState(false);
  const loading =
    isShowLoading ||
    isEpisodeLoading ||
    isUserLoading ||
    isFeaturesPermissionsLoading ||
    isNetworkLoading;
  const { embedPlayerUrl, googleFontApiKey, defaultCover } = config;

  return (
    <>
      {!loading && (
        <ErrorBoundary
          onError={() => {
            message.danger({
              content: (
                <FormattedMessage defaultMessage="We couldn't load the embed customizer. Please, contact the support team." />
              ),
            });
          }}
          fallback={() => null}
        >
          <Suspense
            fallback={createPortal(
              <div className="ant-modal-mask embed-customizer-loader">
                <Loader />
              </div>,
              document.body
            )}
          >
            <Modal
              wrapClassName="embed-player"
              style={{ maxWidth: '1360px' }}
              width="100%"
              title={<FormattedMessage defaultMessage="Embed Audio Player" />}
              centered={true}
              onCancel={() => setModalVisible(false)}
              footer={null}
              open={modalVisible}
              onClose={() => setModalVisible(false)}
            >
              <EmbedCustomizer
                networkData={network}
                showData={show}
                episodeData={episode}
                locale={intl.locale}
                userData={user}
                config={{ embedPlayerUrl, googleFontApiKey, defaultCover }}
                hasFeatureAccess={featuresPermissions?.canCustomizeEmbedPlayer}
              />
            </Modal>
          </Suspense>
        </ErrorBoundary>
      )}

      <div>
        <Button
          loading={loading}
          icon={
            episodeId && episode?.encoding?.status !== 'done' ? (
              <LoadingOutlined />
            ) : (
              <BlockOutlined />
            )
          }
          onClick={() => {
            trackEvent({
              category: 'action-links',
              action: 'click',
              label: episodeId ? 'embed-player-episode' : 'embed-player-show',
            });
            setModalVisible(true);
          }}
        >
          <FormattedMessage defaultMessage="Embed Player" />
        </Button>
      </div>
    </>
  );
};

export default PlayerButton;
